// Dashboard admin: KPIs principales + listas operativas.
//
// 1 sola fetch a /api/admin/dashboard para evitar N llamadas al cargar la home.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  TrendingUp, Users, Calendar, Wallet, AlertCircle, Loader2, UserX, Clock,
} from 'lucide-react';

import { getDashboard } from '../../lib/adminApi';

export default function AdminDashboard() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getDashboard()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) setError(e); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return <div className="admin-loading"><Loader2 size={28} className="spin" /> <span>{t('admin.cargando')}</span></div>;
  }
  if (error) {
    return <div className="admin-error"><AlertCircle size={20} /><span>{error.message}</span></div>;
  }
  if (!data) return null;

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h1>{t('admin.dashboard.titulo')}</h1>
        <p className="admin-page-sub">{t('admin.dashboard.sub')}</p>
      </header>

      <section className="admin-kpis">
        <div className="kpi">
          <div className="kpi-icon"><Calendar size={20} strokeWidth={1.5} /></div>
          <div className="kpi-num">{data.proximas_24h.total}</div>
          <div className="kpi-label">{t('admin.dashboard.kpi.proximas_24h')}</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon"><TrendingUp size={20} strokeWidth={1.5} /></div>
          <div className="kpi-num">{data.ocupacion_semana.ocupacion_pct}%</div>
          <div className="kpi-label">{t('admin.dashboard.kpi.ocupacion')}</div>
          <div className="kpi-foot">
            {data.ocupacion_semana.reservas_activas} / {data.ocupacion_semana.plazas_totales}
          </div>
        </div>
        <div className="kpi">
          <div className="kpi-icon"><Wallet size={20} strokeWidth={1.5} /></div>
          <div className="kpi-num">{data.ingresos_mes.total_euros.toFixed(0)} €</div>
          <div className="kpi-label">{t('admin.dashboard.kpi.ingresos_mes')}</div>
          <div className="kpi-foot">{data.ingresos_mes.total_pagos} {t('admin.dashboard.kpi.pagos')}</div>
        </div>
        <div className="kpi">
          <div className="kpi-icon"><Users size={20} strokeWidth={1.5} /></div>
          <div className="kpi-num">{data.alumnos_nuevos_mes.total}</div>
          <div className="kpi-label">{t('admin.dashboard.kpi.alumnos_nuevos')}</div>
        </div>
        {data.tasa_no_show_mes && (
          <div className={'kpi' + (data.tasa_no_show_mes.tasa_pct >= 20 ? ' kpi--warn' : '')}>
            <div className="kpi-icon"><UserX size={20} strokeWidth={1.5} /></div>
            <div className="kpi-num">{data.tasa_no_show_mes.tasa_pct}%</div>
            <div className="kpi-label">{t('admin.dashboard.kpi.no_show')}</div>
            <div className="kpi-foot">
              {data.tasa_no_show_mes.no_asistio} / {data.tasa_no_show_mes.total_resueltas}
            </div>
          </div>
        )}
        {data.bonos_caducando_30d && (
          <div className={'kpi' + (data.bonos_caducando_30d.total > 0 ? ' kpi--warn' : '')}>
            <div className="kpi-icon"><Clock size={20} strokeWidth={1.5} /></div>
            <div className="kpi-num">{data.bonos_caducando_30d.total}</div>
            <div className="kpi-label">{t('admin.dashboard.kpi.bonos_caducando')}</div>
            <div className="kpi-foot">
              <Link to="/admin/bonos-comprados" className="kpi-link">{t('admin.ver_todas')}</Link>
            </div>
          </div>
        )}
      </section>

      <section className="admin-section">
        <div className="admin-section-head">
          <h2>{t('admin.dashboard.proximas_titulo')}</h2>
          <Link to="/admin/reservas" className="admin-section-link">{t('admin.ver_todas')}</Link>
        </div>
        {data.proximas_24h.data.length === 0 ? (
          <p className="admin-empty">{t('admin.dashboard.sin_proximas')}</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.tabla.codigo')}</th>
                <th>{t('admin.tabla.fecha_hora')}</th>
                <th>{t('admin.tabla.clase')}</th>
                <th>{t('admin.tabla.profesor')}</th>
                <th>{t('admin.tabla.titular')}</th>
                <th>{t('admin.tabla.estado')}</th>
              </tr>
            </thead>
            <tbody>
              {data.proximas_24h.data.map((r) => (
                <tr key={r.id}>
                  <td><Link to={`/admin/reservas?busqueda_cliente=${r.codigo_reserva}`}>{r.codigo_reserva}</Link></td>
                  <td>{r.fecha_clase} · {r.horario?.hora_inicio?.slice(0, 5)}</td>
                  <td>{r.horario?.clase_tipo?.nombre_es}</td>
                  <td>{r.horario?.profesor?.nombre_publico}</td>
                  <td>
                    {r.usuario_id
                      ? `${r.usuario?.nombre || ''} ${r.usuario?.apellidos || ''}`.trim()
                      : `${r.nombre_guest} (${r.email_guest})`}
                  </td>
                  <td><span className={'estado-pill estado-' + r.estado}>{r.estado}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="admin-section">
        <div className="admin-section-head">
          <h2>{t('admin.dashboard.pendientes_titulo')}</h2>
        </div>
        {data.pendientes_pago_estudio.data.length === 0 ? (
          <p className="admin-empty">{t('admin.dashboard.sin_pendientes')}</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>{t('admin.tabla.codigo')}</th>
                <th>{t('admin.tabla.fecha')}</th>
                <th>{t('admin.tabla.clase')}</th>
                <th>{t('admin.tabla.titular')}</th>
                <th>{t('admin.tabla.importe')}</th>
              </tr>
            </thead>
            <tbody>
              {data.pendientes_pago_estudio.data.map((p) => (
                <tr key={p.pago_id}>
                  <td><Link to={`/admin/reservas?busqueda_cliente=${p.reserva.codigo_reserva}`}>{p.reserva.codigo_reserva}</Link></td>
                  <td>{p.reserva.fecha_clase}</td>
                  <td>{p.reserva.horario?.clase_tipo?.nombre_es}</td>
                  <td>
                    {p.reserva.usuario_id
                      ? `${p.reserva.usuario?.nombre || ''} ${p.reserva.usuario?.apellidos || ''}`.trim()
                      : `${p.reserva.nombre_guest} (${p.reserva.email_guest})`}
                  </td>
                  <td>{p.importe_euros} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
